import { z } from 'zod';

import { digitsOnly, isValidCepLength, isValidCpfCnpjLength } from '~/lib/masks';

/**
 * Espelha invoices/serializers.py:InvoiceCreateSerializer. Valida no PWA
 * antes de enviar; o backend volta a validar antes de persistir.
 */

const ufRegex = /^[A-Z]{2}$/;

export const tomadorSchema = z.object({
  cpf_cnpj: z
    .string()
    .trim()
    .min(1, 'Informe o CPF ou CNPJ')
    .refine((value) => isValidCpfCnpjLength(value), 'CPF/CNPJ inválido'),
  razao_social: z.string().trim().min(2, 'Razão social obrigatória').max(255),
  email: z.string().trim().email('E-mail inválido').optional().or(z.literal('')),
  inscricao_municipal: z.string().trim().max(20).optional().or(z.literal('')),
  cep: z
    .string()
    .trim()
    .refine((value) => value === '' || isValidCepLength(value), 'CEP inválido')
    .optional()
    .or(z.literal('')),
  logradouro: z.string().trim().max(255).optional().or(z.literal('')),
  numero: z.string().trim().max(20).optional().or(z.literal('')),
  complemento: z.string().trim().max(100).optional().or(z.literal('')),
  bairro: z.string().trim().max(100).optional().or(z.literal('')),
  codigo_cidade: z.string().trim().max(7).optional().or(z.literal('')),
  descricao_cidade: z.string().trim().max(100).optional().or(z.literal('')),
  estado: z
    .string()
    .trim()
    .optional()
    .or(z.literal(''))
    .refine((value) => !value || ufRegex.test(value.toUpperCase()), 'UF inválida'),
});

export type TomadorInput = z.infer<typeof tomadorSchema>;

export const servicoSchema = z.object({
  codigo_servico: z.string().trim().min(1, 'Selecione o código do serviço'),
  discriminacao: z
    .string()
    .trim()
    .min(5, 'Descreva o serviço prestado (mín. 5 caracteres)')
    .max(2000),
});

export type ServicoInput = z.infer<typeof servicoSchema>;

export const valorSchema = z.object({
  valor_servico: z
    .number({ invalid_type_error: 'Valor inválido' })
    .positive('Informe um valor maior que zero'),
  iss_aliquota: z.number().min(0, 'Alíquota não pode ser negativa').max(100, 'Alíquota inválida'),
  iss_retido: z.boolean(),
  iss_tipo_tributacao: z.number().int().min(1).max(7),
  retencoes: z.object({
    irrf: z.boolean(),
    irrf_aliquota: z.number().min(0).max(100),
    pis: z.boolean(),
    pis_aliquota: z.number().min(0).max(100),
    cofins: z.boolean(),
    cofins_aliquota: z.number().min(0).max(100),
    csll: z.boolean(),
    csll_aliquota: z.number().min(0).max(100),
    inss: z.boolean(),
    inss_aliquota: z.number().min(0).max(100),
  }),
});

export type ValorInput = z.infer<typeof valorSchema>;

export const invoiceDraftSchema = z.object({
  clientId: z.number().int().positive(),
  tomador: tomadorSchema,
  servico: servicoSchema,
  valor: valorSchema,
  updatedAt: z.number().int(),
  /**
   * Identifica a emissão para o backend recusar duplicata. Nasce com o rascunho e
   * não muda: precisa ser a mesma na primeira tentativa e em toda retentativa,
   * senão o servidor lê cada envio como uma nota nova.
   */
  idempotencyKey: z.string().min(1),
});

export type InvoiceDraft = z.infer<typeof invoiceDraftSchema>;

export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Rascunhos salvos antes desta versão não têm chave. Preenche na hidratação para
 * que a emissão saia protegida mesmo vindo de um rascunho antigo.
 */
export function ensureIdempotencyKey(draft: InvoiceDraft): InvoiceDraft {
  if (draft.idempotencyKey) return draft;
  return { ...draft, idempotencyKey: createIdempotencyKey() };
}

export const DEFAULT_RETENCOES: ValorInput['retencoes'] = {
  irrf: false,
  irrf_aliquota: 1.5,
  pis: false,
  pis_aliquota: 0.65,
  cofins: false,
  cofins_aliquota: 3.0,
  csll: false,
  csll_aliquota: 1.0,
  inss: false,
  inss_aliquota: 11.0,
};

export const DEFAULT_VALOR: ValorInput = {
  valor_servico: 0,
  iss_aliquota: 5,
  iss_retido: false,
  iss_tipo_tributacao: 6,
  retencoes: DEFAULT_RETENCOES,
};

export const DEFAULT_TOMADOR: TomadorInput = {
  cpf_cnpj: '',
  razao_social: '',
  email: '',
  inscricao_municipal: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  codigo_cidade: '',
  descricao_cidade: '',
  estado: '',
};

export const DEFAULT_SERVICO: ServicoInput = {
  codigo_servico: '',
  discriminacao: '',
};

export function buildEmptyDraft(clientId: number): InvoiceDraft {
  return {
    clientId,
    tomador: DEFAULT_TOMADOR,
    servico: DEFAULT_SERVICO,
    valor: DEFAULT_VALOR,
    updatedAt: Date.now(),
    idempotencyKey: createIdempotencyKey(),
  };
}

/**
 * Monta o payload final que vai pra POST /api/invoices/, já com CPF/CNPJ/CEP
 * em dígitos puros (o backend faz o mesmo no Alpine antigo).
 */
export interface InvoiceApiPayload {
  client: number;
  tomador_cpf_cnpj: string;
  tomador_razao_social: string;
  tomador_email: string;
  tomador_inscricao_municipal: string;
  tomador_cep: string;
  tomador_logradouro: string;
  tomador_numero: string;
  tomador_complemento: string;
  tomador_bairro: string;
  tomador_codigo_cidade: string;
  tomador_descricao_cidade: string;
  tomador_estado: string;
  is_exportacao: boolean;
  items: InvoiceItemApiPayload[];
}

export interface InvoiceItemApiPayload {
  codigo_servico: string;
  discriminacao: string;
  valor_servico: number;
  iss_retido: boolean;
  iss_aliquota: number;
  iss_tipo_tributacao: number;
  iss_tipo_retencao: number;
  iss_exigibilidade: number;
  retencao_irrf: boolean;
  retencao_irrf_aliquota: number;
  retencao_pis: boolean;
  retencao_pis_aliquota: number;
  retencao_cofins: boolean;
  retencao_cofins_aliquota: number;
  retencao_csll: boolean;
  retencao_csll_aliquota: number;
  retencao_inss: boolean;
  retencao_inss_aliquota: number;
}

export function buildInvoicePayload(draft: InvoiceDraft): InvoiceApiPayload {
  const { tomador, servico, valor } = draft;
  return {
    client: draft.clientId,
    tomador_cpf_cnpj: digitsOnly(tomador.cpf_cnpj),
    tomador_razao_social: tomador.razao_social,
    tomador_email: tomador.email ?? '',
    tomador_inscricao_municipal: tomador.inscricao_municipal ?? '',
    tomador_cep: digitsOnly(tomador.cep),
    tomador_logradouro: tomador.logradouro ?? '',
    tomador_numero: tomador.numero ?? '',
    tomador_complemento: tomador.complemento ?? '',
    tomador_bairro: tomador.bairro ?? '',
    tomador_codigo_cidade: tomador.codigo_cidade ?? '',
    tomador_descricao_cidade: tomador.descricao_cidade ?? '',
    tomador_estado: (tomador.estado ?? '').toUpperCase(),
    is_exportacao: false,
    items: [
      {
        codigo_servico: servico.codigo_servico,
        discriminacao: servico.discriminacao,
        valor_servico: valor.valor_servico,
        iss_retido: valor.iss_retido,
        iss_aliquota: valor.iss_aliquota,
        iss_tipo_tributacao: valor.iss_tipo_tributacao,
        iss_tipo_retencao: valor.iss_retido ? 2 : 1,
        iss_exigibilidade: 1,
        retencao_irrf: valor.retencoes.irrf,
        retencao_irrf_aliquota: valor.retencoes.irrf_aliquota,
        retencao_pis: valor.retencoes.pis,
        retencao_pis_aliquota: valor.retencoes.pis_aliquota,
        retencao_cofins: valor.retencoes.cofins,
        retencao_cofins_aliquota: valor.retencoes.cofins_aliquota,
        retencao_csll: valor.retencoes.csll,
        retencao_csll_aliquota: valor.retencoes.csll_aliquota,
        retencao_inss: valor.retencoes.inss,
        retencao_inss_aliquota: valor.retencoes.inss_aliquota,
      },
    ],
  };
}
